import { parseISO } from "date-fns";
import { Check, ChevronLeft, Copy, Pencil, Power, PowerOff } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { ProductDetailUI } from "~/features/admin/products/types";
import { WebhookDocSection } from "./WebhookDocSection";

interface ProductDetailPresenterProps {
  product: ProductDetailUI;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  pendingAction: "activate" | "deactivate" | null;
  confirmLabel: string;
  processing: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
}

export function ProductDetailPresenter({
  product,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  pendingAction,
  confirmLabel,
  processing,
  onConfirm,
  onCancelConfirm,
}: ProductDetailPresenterProps) {
  const { t } = useTranslation();

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.name"), value: product.name },
    { label: t("model.product.originHosts"), value: product.originHosts },
    { label: t("model.product.importDirectWebhookUrl"), value: product.importDirectWebhookUrl ?? "—" },
    { label: t("model.product.availableRoles"), value: product.availableRoles.join(", ") || "—" },
  ];

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-dewey-title">{product.name}</h1>
          {product.deactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {product.deactivatedAt ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader product={product} />

      <section className="space-y-2">
        <h2 className="text-16-bold capitalize leading-tight">{t("model.product.modelName")}</h2>
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
                  <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left align-top text-12-medium text-fg-2">
                    {row.label}
                  </th>
                  <td className="px-3.5 py-2 text-14-regular text-fg-1">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <WebhookDocSection />

      <AlertDialog open={pendingAction !== null} onOpenChange={(next) => !next && onCancelConfirm()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.common.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{confirmLabel}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm} disabled={processing}>
              {pendingAction === "activate" ? t("ui.activate") : t("ui.deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubHeader({ product }: { product: ProductDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.id"), value: <CopyableText text={product.id} className="font-mono" /> },
  ];
  if (product.createdAt) {
    items.push({ label: t("model.common.createdAt"), value: t("date.long", { date: parseISO(product.createdAt) }) });
  }
  if (product.updatedAt && product.updatedAt !== product.createdAt) {
    items.push({ label: t("model.common.updatedAt"), value: t("date.long", { date: parseISO(product.updatedAt) }) });
  }
  if (product.deactivatedAt) {
    items.push({
      label: t("model.common.deactivatedAt"),
      value: t("date.long", { date: parseISO(product.deactivatedAt) }),
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}
